import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import i18n from 'src/i18n'
import { useDecision } from '@optimizely/react-sdk'
import { PostCommentItem } from '../postCommentItem'
import { usePostRepliesComments } from '../../hooks'
import { PathParams, PostRepliesCommentProps } from './postRepliesComment.types'
import { MAX_REPLIES_PER_PAGE } from './postRepliesComment.constants'

const PostRepliesComment = ({
  comment,
  ownerCollectionId,
  canUserRespondToComment
}: PostRepliesCommentProps) => {
  const { idPost } = useParams() as PathParams
  const [commentMultipleReplies] = useDecision('com_eng_web_enable_comment_multiple_replies')
  const sortOrder = commentMultipleReplies?.enabled ? 'asc' : 'desc'
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } =
    usePostRepliesComments({
      postId: Number(idPost),
      commentId: comment.id,
      repliesCommentsPerPage: MAX_REPLIES_PER_PAGE,
      sort: sortOrder
    })

  const replies = useMemo(() => {
    return data?.pages.flatMap((page) => page.data)
  }, [data])

  const hasValidReplies = Array.isArray(replies) && replies?.length > 0
  const showReplies = hasValidReplies && !isLoading

  const handleLoadMoreReplies = () => {
    if (isFetchingNextPage) return

    const pages = data?.pages
    const lastPage = pages?.[pages.length - 1]
    const nextPageToken = lastPage?.nextPageToken

    fetchNextPage({ pageParam: nextPageToken })
  }

  useEffect(() => {
    refetch()
  }, [comment])

  if (!showReplies) {
    return null
  }

  return (
    <div>
      {replies.map((reply) => (
        <PostCommentItem
          key={reply.id}
          postId={Number(idPost)}
          comment={reply}
          ownerCollectionId={ownerCollectionId}
          canUserRespondToComment={canUserRespondToComment}
          isReply
          onReloadComments={refetch}
        />
      ))}
      {hasNextPage && (
        <div className="_mt-5">
          <span
            className="_text-gray-900 _text-2 _text-decoration-underline _cursor-pointer"
            onClick={handleLoadMoreReplies}
          >
            {i18n.t('comments.show_more_answers')}
          </span>
        </div>
      )}
    </div>
  )
}

export default PostRepliesComment
